import React, { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import { Text, Modal } from "@mantine/core";
import { IconPaperclip, IconEye, IconTrash, IconUpload } from "@tabler/icons";
import { message } from "antd";
import { isImageFile, isPdfFile } from "../../helpers/utilities";
import { BILL_ATTACHMENT_LIMIT } from "../../helpers/config";
import pdfIcon from "../../static/icons/pdf.png";

const fileNameFromUrl = (url) => {
  try {
    const seg = url.split("/").pop();
    return seg?.split("?")[0] || "Attachment";
  } catch {
    return "Attachment";
  }
};

export const BillAttachments = ({
  attachments = [],
  pendingFiles = [],
  maxTotal = BILL_ATTACHMENT_LIMIT,
  onFilesChange,
  onRemoveAttachment,
  readOnly = false,
}) => {
  const [preview, setPreview] = useState({ open: false, url: "", name: "" });
  const total = attachments.length + pendingFiles.length;
  const canAdd = total < maxTotal && !readOnly;

  const onDrop = useCallback(
    (accepted, rejected) => {
      if (rejected?.length) message.error("Some files were rejected (images or PDF only).");
      if (!accepted.length) return;
      const remaining = maxTotal - attachments.length - pendingFiles.length;
      const toAdd = accepted.slice(0, remaining);
      onFilesChange?.([...pendingFiles, ...toAdd]);
    },
    [attachments.length, maxTotal, onFilesChange, pendingFiles]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    maxSize: 10 * 1024 * 1024,
    accept: { "image/*": [], "application/pdf": [] },
    multiple: true,
    disabled: !canAdd,
  });

  const removePending = (index) => {
    const next = pendingFiles.filter((_, i) => i !== index);
    onFilesChange?.(next);
  };

  const openPreview = (url, name) => {
    const fn = name || fileNameFromUrl(url);
    if (isPdfFile(fn)) window.open(url, "_blank");
    else setPreview({ open: true, url, name: fn });
  };

  const renderFileRow = (item, index, isPending) => {
    const url = item.fileUrl || (isPending && item instanceof File ? URL.createObjectURL(item) : null);
    const name = isPending && item instanceof File ? item.name : fileNameFromUrl(item.fileUrl || "");
    const displayName = name.length > 20 ? name.slice(0, 20) + "…" : name;
    const isImg = isImageFile(name);
    const isPdf = isPdfFile(name);

    return (
      <div
        key={isPending ? `p-${index}` : item.id}
        role={url ? "button" : undefined}
        tabIndex={url ? 0 : undefined}
        className={`d-flex align-items-center border rounded p-2 mb-2 bg-light ${url ? "cursor-pointer" : ""}`}
        style={url ? { cursor: "pointer" } : undefined}
        onClick={url ? () => openPreview(url, name) : undefined}
        onKeyDown={url ? (e) => e.key === "Enter" && openPreview(url, name) : undefined}
      >
        <div className="me-2 flex-shrink-0" style={{ width: 40, height: 40 }}>
          {isImg && url ? (
            <img src={url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 4 }} />
          ) : isPdf ? (
            <img src={pdfIcon} alt="PDF" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
          ) : (
            <div className="d-flex align-items-center justify-content-center bg-white rounded h-100">
              <IconPaperclip size={20} className="text-muted" />
            </div>
          )}
        </div>
        <div className="flex-grow-1 min-w-0">
          <Text size="sm" truncate title={name}>{displayName}</Text>
          {isPending && item instanceof File && (
            <Text size="xs" color="dimmed">{(item.size / 1024).toFixed(1)} KB</Text>
          )}
        </div>
        {!readOnly && (
          <div className="d-flex align-items-center gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
            {url && (
              <button type="button" className="button is-small is-light is-info" title="View" onClick={() => openPreview(url, name)}>
                <IconEye size={16} />
              </button>
            )}
            <button
              type="button"
              className="button is-small is-light is-danger"
              title="Remove"
              onClick={() => (isPending ? removePending(index) : onRemoveAttachment?.(item))}
            >
              <IconTrash size={16} />
            </button>
          </div>
        )}
      </div>
    );
  };

  const allFiles = [
    ...attachments.map((a, i) => ({ item: a, index: i, isPending: false })),
    ...pendingFiles.map((f, i) => ({ item: f, index: i, isPending: true })),
  ];

  return (
    <div className="bill-attachments">
      {/* Row 1: Drag and drop zone */}
      {canAdd && (
        <div
          {...getRootProps()}
          className="border rounded p-4 mb-3"
          style={{
            borderStyle: "dashed",
            borderWidth: 2,
            borderColor: "var(--bs-gray-400)",
            minHeight: 100,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
          }}
        >
          <input {...getInputProps()} />
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center" }}>
            <IconUpload size={36} className="text-muted mb-2" />
            <Text size="sm" color="dimmed">
              {isDragActive ? "Drop files here" : "Drag files here or click to select"}
            </Text>
            <Text size="xs" color="dimmed">Images & PDF · Max 10MB · Up to {maxTotal} files</Text>
          </div>
        </div>
      )}

      {/* Row n: List of attached files */}
      {allFiles.length > 0 && (
        <div>
          <Text size="sm" weight={600} className="mb-2">Attached ({total}/{maxTotal})</Text>
          {allFiles.map(({ item, index, isPending }) => renderFileRow(item, index, isPending))}
        </div>
      )}

      <Modal opened={preview.open} onClose={() => setPreview((p) => ({ ...p, open: false }))} title={preview.name} size="lg">
        {preview.url && (
          <img src={preview.url} alt={preview.name} style={{ maxWidth: "100%", height: "auto" }} />
        )}
      </Modal>
    </div>
  );
};
